import React, {
  forwardRef,
  useRef,
  useEffect,
  useState,
  useContext,
} from 'react'
import { Link } from 'react-router-dom'
import cn from 'classnames'
import Select from 'react-select'
import { Formik, useField, FieldArray, Field } from 'formik'
import * as Yup from 'yup'
import countries from 'iso-3166-country-list'
import { FilePond } from 'react-filepond'
import { Player, AudioProvider, AudioContext } from 'rewact'
import { motion, AnimatePresence } from 'framer-motion'

import * as Icon from './Icons'
import { authRequest } from './api'

import genre from './filters/genre.json'
import vocals from './filters/vocals.json'
import tempo from './filters/tempo.json'
import { tracksByArtists } from './artistSchema'

import styles from './form.module.css'
import 'filepond/dist/filepond.min.css'

const initialPack = { name: null, tracks: [] }
function Form({
  requestType = 'supervision',
  userId,
  initialValues = {},
  onSubmit,
}) {
  const validationSchema = createValidationSchema(requestType)

  const [currentTrack, setCurrentTrack] = useState({
    artist: '',
    name: '',
    slug: '',
  })

  const [tracksPacks, setTracksPacks] = useState([])
  const [selectedPack, setSelectedPack] = useState(initialPack)

  useEffect(() => {
    if (initialValues.trackSlug) {
      setCurrentTrack({
        artist: initialValues.trackArtist,
        name: initialValues.trackName,
        slug: initialValues.trackSlug,
      })
    }
  }, [initialValues])

  const TRACKS_PACKS_OPTS = tracksPacks.map(({ name, tracks }) => ({
    label: name,
    value: tracks,
  }))

  useEffect(() => {
    if (requestType !== 'purchased') {
      authRequest(`/admin/tracks-packs`)
        // .then((res) => res.json())
        .then(setTracksPacks)
        .catch(console.log)
    }
  }, [requestType, selectedPack])

  function saveTracks(_tracks) {
    const tracks = _tracks.map(getTrackEssentialInfo)
    authRequest(`/admin/tracks-packs`, {
      method: 'POST',
      body: JSON.stringify({
        tracks,
      }),
      // headers: {
      //   'Content-Type': 'application/json',
      // },
    })
      // .then((res) => res.json())
      .then(setSelectedPack)
      .catch((err) => console.log({ err }))
  }

  function removePack(name) {
    authRequest(`/admin/tracks-packs`, {
      method: 'DELETE',
      body: JSON.stringify({
        name,
      }),
      // headers: {
      //   'Content-Type': 'application/json',
      // },
    })
      // .then((res) => res.json())
      .then(() => setSelectedPack(initialPack))
      .catch((err) => console.log({ err }))
  }

  return (
    <AudioProvider src={`/track/${currentTrack.slug}.mp3`}>
      <Formik
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={initialValues}
        validateOnBlur
        validateOnChange
        enableReinitialize
      >
        {({
          isSubmitting,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          values,
          dirty,
          resetForm,
        }) => {
          return (
            <form onSubmit={handleSubmit} className={styles.grid}>
              {requestType === 'purchased' ? (
                <>
                  <fieldset className={styles.fieldset}>
                    <InputField
                      name="date"
                      placeholder="Purchased At"
                      disabled
                    />
                    <InputField
                      name="refRequestType"
                      placeholder="Ref. Request Type"
                      disabled
                    />
                    <InputField
                      name="refRequestId"
                      placeholder="Ref. Request ID"
                      disabled
                    />
                  </fieldset>
                  <fieldset className={styles.fieldset}>
                    <InputField name="licensor" placeholder="Licensor" />
                    <InputField
                      name="licensorAddress"
                      placeholder="Licensor Address"
                    />
                    <InputField name="useLength" placeholder="Use Length" />
                    <InputField name="cutDown" placeholder="Cut Down" />
                    <InputField name="projectName" placeholder="ProjectName" />
                    <InputField name="address" placeholder="Address" />
                  </fieldset>
                  <fieldset className={styles.fieldset}>
                    <InputField
                      name="paymentMethod"
                      placeholder="Payment Method"
                      disabled
                    />
                    <InputField
                      name="transactionId"
                      placeholder="Transaction Id"
                      disabled
                    />
                    <InputField
                      name="trackPrice"
                      placeholder="Track Price"
                      disabled
                    />
                    <InputField
                      name="trackName"
                      placeholder="Track Name"
                      disabled
                    />
                    <InputField
                      name="trackArtist"
                      placeholder="Track Artist"
                      disabled
                    />
                    <InputField name="syncId" placeholder="Sync Id" />
                    <label>
                      <Field type="checkbox" name="syncIdVerified" /> Sync
                      Verified?
                    </label>
                    <InputField
                      name="trackSlug"
                      placeholder="Track Slug"
                      type="hidden"
                      disabled
                    />

                    <InputField name="file" placeholder="High Quality File" />
                    <Uploader
                      updateFilePath={(serverId, ext) => {
                        setFieldValue(
                          'file',
                          `${
                            process.env.NODE_ENV === 'production'
                              ? 'https://music.pixeden.com'
                              : 'http://localhost:5011'
                          }/purchased/${userId}/${serverId}.${ext}`
                        )
                        setFieldValue('serverId', serverId)
                      }}
                    />
                    <InputField
                      name="serverId"
                      placeholder="Server Id"
                      type="hidden"
                      disabled
                    />
                  </fieldset>
                </>
              ) : (
                <>
                  <fieldset className={styles.fieldset}>
                    <InputField name="date" placeholder="Created At" disabled />
                    <InputField name="company" placeholder="Company" />
                    <InputField
                      name="projectName"
                      placeholder="Project Name *"
                    />
                    <InputField
                      name="licenseUser"
                      placeholder="Licensee"
                      label="End Client"
                    />
                    {requestType === 'supervision' && (
                      <>
                        <SelectField
                          name="style"
                          options={STYLE_OPTS}
                          placeholder="Music Style"
                          onBlur={() => setFieldTouched('style', true)}
                          isMulti
                        />
                        <SelectField
                          name="vocals"
                          options={VOCALS_OPTS}
                          placeholder="Vocals"
                          onBlur={() => setFieldTouched('vocals', true)}
                          isSearchable={false}
                          isMulti
                        />
                        <SelectField
                          name="tempo"
                          options={TEMPO_OPTS}
                          placeholder="Tempo"
                          onBlur={() => setFieldTouched('tempo', true)}
                          isSearchable={false}
                          isMulti
                        />
                        <InputField
                          name="reference"
                          placeholder="Reference"
                          label="link youtube, link spotify"
                        />
                      </>
                    )}
                  </fieldset>
                  <fieldset className={styles.fieldset}>
                    <SelectField
                      name="media"
                      options={MEDIA_OPTS}
                      placeholder="Media"
                      isMulti
                    />
                    {values.media &&
                      values.media.some(
                        (value) => value === 'Online / Web / Streaming'
                      ) && (
                        <SelectField
                          name="paidMedia"
                          options={PAIDMEDIA_OPTS}
                          placeholder="Paid Media"
                          isSearchable={false}
                        />
                      )}
                    <SelectField
                      name="projectType"
                      options={PROJECT_TYPE_OPTS}
                      placeholder="Type of Project"
                      isMulti
                    />
                    <SelectField
                      name="territory"
                      options={TERRITORY_OPTS}
                      placeholder="Territory"
                      isSearchable={false}
                      isMulti
                    />
                    {values.territory &&
                      values.territory.some(
                        (value) => value === 'National'
                      ) && (
                        <SelectField
                          name="country"
                          options={COUNTRIES_OPTS}
                          placeholder="Country"
                          isMulti
                        />
                      )}
                    <SelectField
                      name="licensingTerm"
                      options={LICENSINGTERM_OPTS}
                      placeholder="Licensing Term"
                      isSearchable={false}
                    />
                    <InputField name="budget" placeholder="Budget" />
                  </fieldset>
                  <fieldset className={styles.fieldset}>
                    <SelectField
                      name="typeOfUse"
                      options={TYPEOFUSE_OPTS}
                      placeholder="Type of Use"
                      isSearchable={false}
                    />
                    <SelectField
                      name="typeOfLicense"
                      options={TYPEOFLICENSE_OPTS}
                      placeholder="Type of License"
                      isSearchable={false}
                    />
                    <TextareaField
                      name="extraInfo"
                      placeholder="Extra information"
                      label="Tell us about your project, we are here to help =)"
                    />
                  </fieldset>
                  <h3 className={styles.subtitle}>Tracks</h3>
                  <FieldArray
                    name="tracks"
                    render={(arrayHelpers) => (
                      <>
                        <fieldset className={styles.subgrid}>
                          {values.tracks &&
                            values.tracks.length > 0 &&
                            values.tracks.map((track, index) => {
                              const _slug =
                                track.artist && track.name
                                  ? tracksByArtists[track.artist].find(
                                      ({ name }) => name === track.name
                                    ).slug
                                  : null
                              return (
                                <div
                                  key={index}
                                  style={{ marginBottom: '2em' }}
                                >
                                  <SelectField
                                    name={`tracks.${index}.artist`}
                                    options={ARTIST_OPT}
                                    placeholder="Track Artist"
                                    isDisabled={track.purchased && track.artist}
                                    onChange={() => {
                                      setFieldValue(
                                        `tracks.${index}.name`,
                                        null
                                      )
                                    }}
                                  />
                                  {track.artist && (
                                    <SelectField
                                      name={`tracks.${index}.name`}
                                      options={tracksByArtists[
                                        track.artist
                                      ].map(({ name }) => ({
                                        label: name,
                                        value: name,
                                      }))}
                                      placeholder="Track Name"
                                      isDisabled={track.purchased && track.name}
                                      onChange={(value) => {
                                        setFieldValue(
                                          `tracks.${index}.slug`,
                                          tracksByArtists[track.artist].find(
                                            ({ name }) => name === value
                                          ).slug
                                        )
                                        setFieldValue(
                                          `tracks.${index}.image`,
                                          tracksByArtists[track.artist].find(
                                            ({ name }) => name === value
                                          ).image
                                        )
                                      }}
                                    />
                                  )}
                                  <InputField
                                    name={`tracks.${index}.price`}
                                    placeholder="Track Price"
                                    disabled={track.purchased && track.price}
                                  />
                                  {track.name && (
                                    <InputField
                                      name={`tracks.${index}.slug`}
                                      value={
                                        tracksByArtists[track.artist].find(
                                          ({ name }) => name === track.name
                                        ).slug
                                      }
                                      placeholder="Track Slug"
                                      disabled
                                      type="hidden"
                                    />
                                  )}
                                  {track.name && (
                                    <InputField
                                      name={`tracks.${index}.image`}
                                      value={
                                        tracksByArtists[track.artist].find(
                                          ({ name }) => name === track.name
                                        ).image
                                      }
                                      placeholder="Track Image"
                                      disabled
                                      type="hidden"
                                    />
                                  )}
                                  <label>
                                    <Field
                                      type="checkbox"
                                      name={`tracks.${index}.purchased`}
                                    />{' '}
                                    Purchased?
                                  </label>{' '}
                                  {track.purchased && track.purchasedId && (
                                    <Link
                                      to={`/user/${userId}/purchased/${track.purchasedId}`}
                                    >
                                      View Purchased Item
                                    </Link>
                                  )}
                                  {track.purchased && track.purchasedOn && (
                                    <InputField
                                      name={`tracks.${index}.purchasedOn`}
                                      placeholder="Purchased Date"
                                      disabled
                                    />
                                  )}
                                  <hr />
                                  {track.name && (
                                    <PreviewButton
                                      isSelected={
                                        currentTrack.name === track.name
                                      }
                                      setCurrentTrack={() => {
                                        setCurrentTrack({
                                          artist: track.artist,
                                          name: track.name,
                                          slug: _slug,
                                        })
                                      }}
                                    />
                                  )}
                                  <button
                                    type="button"
                                    className={styles.button}
                                    onClick={() => {
                                      arrayHelpers.remove(index)
                                      setSelectedPack(initialPack)
                                    }}
                                  >
                                    Remove Track
                                  </button>
                                  {currentTrack.slug !== '' &&
                                    currentTrack.slug === _slug && (
                                      <AnimatePresence>
                                        <motion.div
                                          drag
                                          className={styles.tinyPlayerWrapper}
                                          animate={{
                                            opacity: 1,
                                          }}
                                          exit={{ opacity: 0 }}
                                          initial={{
                                            opacity: 0,
                                          }}
                                          transition={{
                                            duration: 0.3,
                                            delay: 0.2,
                                          }}
                                        >
                                          <Player
                                            artist={currentTrack.artist}
                                            name={currentTrack.name}
                                            hidden={['prev', 'next']}
                                            className={styles.tinyPlayer}
                                            styles={{
                                              wrapper: (base) => ({
                                                ...base,
                                                position: 'relative',
                                                marginTop: '2em',
                                                width: '85%',
                                                '--rw-columns':
                                                  '1fr 0.5fr 0 0.3fr 0.2fr !important',
                                                '--rw-width': '90% !important',
                                                boxShadow:
                                                  '0 10px 10px rgba(0, 0, 0, 0.3)',
                                                borderRadius: 16,
                                              }),
                                              slider: () => ({
                                                display: 'none',
                                              }),
                                            }}
                                          >
                                            <button
                                              className={styles.closePlayerBtn}
                                              onClick={() => {
                                                setCurrentTrack({
                                                  artist:
                                                    initialValues.trackArtist,
                                                  name: initialValues.trackName,
                                                  slug: initialValues.trackSlug,
                                                })
                                              }}
                                            >
                                              <Icon.Close />
                                            </button>
                                          </Player>
                                        </motion.div>
                                      </AnimatePresence>
                                    )}
                                </div>
                              )
                            })}
                        </fieldset>
                        <hr
                          style={{
                            gridColumn: '1 / -1',
                            borderTop: '1px solid #f8f5f0',
                            width: '100%',
                          }}
                        />

                        <button
                          type="button"
                          className={styles.button}
                          style={{ '--bg': '#2c8', alignSelf: 'baseline' }}
                          onClick={() => {
                            arrayHelpers.push({})
                            setSelectedPack(initialPack)
                          }}
                        >
                          Add a Track
                        </button>
                        {values.tracks &&
                          values.tracks.length > 0 &&
                          JSON.stringify(selectedPack.tracks) !==
                            JSON.stringify(
                              values.tracks.map(getTrackEssentialInfo)
                            ) && (
                            <button
                              type="button"
                              className={styles.button}
                              style={{ '--bg': '#7af', alignSelf: 'baseline' }}
                              onClick={() => {
                                saveTracks(values.tracks)
                              }}
                            >
                              Save Tracks as Pack
                            </button>
                          )}
                        {selectedPack.name && selectedPack.tracks.length > 0 && (
                          <button
                            type="button"
                            className={styles.button}
                            onClick={() => {
                              removePack(selectedPack.name)
                            }}
                          >
                            Remove Pack
                            <br />
                            <small>{selectedPack.name}</small>
                          </button>
                        )}
                        {tracksPacks.length > 0 && (
                          /*(!values.tracks || values.tracks.length === 0) && */ <Select
                            options={TRACKS_PACKS_OPTS}
                            placeholder="Select a Pack of Tracks…"
                            value={TRACKS_PACKS_OPTS.find(
                              (option) =>
                                JSON.stringify(option.value) ===
                                JSON.stringify(selectedPack.tracks)
                            )}
                            onChange={({ label, value }) => {
                              setFieldValue('tracks', value)
                              setSelectedPack({ name: label, tracks: value })
                            }}
                          />
                        )}
                      </>
                    )}
                  />
                </>
              )}
              <footer className={styles.formFooter}>
                <SubmitButton disabled={!dirty || isSubmitting}>
                  {!dirty
                    ? 'Updated'
                    : isSubmitting
                    ? 'Submitting…'
                    : 'Submit Changes'}
                </SubmitButton>

                <button
                  className={styles.button}
                  type="button"
                  onClick={() => {
                    resetForm()
                    setSelectedPack(initialPack)
                  }}
                  style={{ '--bg': '#75c' }}
                  disabled={!dirty}
                >
                  Reset Changes
                </button>
              </footer>
            </form>
          )
        }}
      </Formik>
    </AudioProvider>
  )
}

export default Form

function getTrackEssentialInfo({
  purchasedId,
  purchased,
  purchasedOn,
  ...trackInfo
}) {
  return trackInfo
}

export const createValidationSchema = (type = 'supervision') => {
  return type !== 'purchased'
    ? Yup.object().shape({
        projectName: Yup.string().required('Project Name is Required'),
        media: Yup.array().nullable().required('Pick at least 1 Paid Media'),
        projectType: Yup.array()
          .nullable()
          .required('Pick at least 1 Project Type'),
        territory: Yup.array().nullable().required('Pick at least 1 Region'),
        style: Yup.array()
          .nullable()
          .required(
            type === 'supervision' ? 'Pick at least 1 Music Style' : false
          ),
        tracks: Yup.array()
          .of(
            Yup.object().shape({
              artist: Yup.string().required('Required'),
              name: Yup.string().required('Required').nullable(),
              price: Yup.number().nullable(),
            })
          )
          .nullable(),
      })
    : null
}

function Uploader({ userId, requestId, updateFilePath }) {
  const ref = useRef(null)
  const [files, setFiles] = useState([])

  return (
    <div style={{ width: 300, height: 60, marginLeft: '1em' }}>
      <FilePond
        ref={ref}
        files={files}
        name="file"
        allowMultiple={false}
        server={`/admin/upload?userId=${userId}&id=${requestId}`}
        onupdatefiles={(fileItems) => {
          setFiles(fileItems.map((fileItem) => fileItem.file))
        }}
        onprocessfile={(error, file) => {
          updateFilePath(file.serverId, file.fileExtension)
        }}
      />
    </div>
  )
}

export function SubmitButton({ disabled, children }) {
  return (
    <button className={styles.button} type="submit" disabled={disabled}>
      {children || 'Submit'}
    </button>
  )
}

export function SelectField({ label, labelStyle, ...props }) {
  const [field, meta, helpers] = useField(props.name)
  const { options, isMulti } = props

  const getValue = () => {
    if (options) {
      return isMulti
        ? options.filter((option) =>
            field.value ? field.value.indexOf(option.value) >= 0 : false
          )
        : options.find((option) => option.value === field.value)
    } else {
      return isMulti ? [] : ''
    }
  }

  const onChange = (option) => {
    helpers.setValue(
      isMulti ? (option ? option.map((item) => item.value) : []) : option.value
    )
    if (props.onChange && typeof props.onChange === 'function') {
      props.onChange(option.value)
    }
  }

  return (
    <label className={styles.label} style={labelStyle}>
      <Select
        className={styles.select}
        styles={{
          control: (base) => ({
            ...base,
            boxShadow: 'none',
            borderColor: 'hsl(0, 0%, 80%)',
            '&:hover': {
              borderColor: 'hsl(0, 0%, 80%)',
            },
          }),
          container: (base) => ({
            ...base,
            marginBottom: 0,
          }),
        }}
        {...field}
        {...props}
        value={getValue()}
        onChange={onChange}
        onBlur={() => helpers.setTouched(true)}
      />
      {meta.error ? (
        <span className={cn(styles.errorMsg, styles.selectMeta)}>
          {meta.error}
        </span>
      ) : null}
      {(!meta.value || meta.value === '') && !meta.error ? (
        <span className={cn(styles.labelMsg, styles.selectMeta)}>{label}</span>
      ) : null}
      {meta.value && !meta.error && (
        <span className={cn(styles.labelMsg, styles.selectMeta)}>
          {props.placeholder}
        </span>
      )}
    </label>
  )
}

export const InputField = forwardRef(
  ({ label, labelStyle, children, ...props }, ref) => {
    const [field, meta] = useField(props)
    return (
      <label
        className={cn(styles.label, {
          [styles.hidden]: props.type === 'hidden',
          [styles.disabled]: props.disabled,
        })}
        style={labelStyle}
      >
        <input
          className={cn(styles.input, {
            [styles.inputError]: meta.touched && meta.error,
          })}
          ref={ref}
          {...field}
          {...props}
        />
        {meta.error ? (
          <span className={styles.errorMsg}>{meta.error}</span>
        ) : null}
        {(!meta.value || meta.value === '') && !meta.error ? (
          <span className={styles.labelMsg}>{label}</span>
        ) : null}
        {meta.value && !meta.error && (
          <span className={styles.labelMsg}>{props.placeholder}</span>
        )}
        {children}
      </label>
    )
  }
)

export function TextareaField({ label, ...props }) {
  const [field, meta] = useField(props)
  return (
    <label className={styles.label}>
      <textarea
        className={cn(styles.input, styles.textarea, {
          [styles.inputError]: meta.touched && meta.error,
        })}
        {...field}
        {...props}
      />
      {meta.error ? (
        <span className={styles.errorMsg}>{meta.error}</span>
      ) : null}
      {(!meta.value || meta.value === '') && !meta.error ? (
        <span className={styles.labelMsg}>{label}</span>
      ) : null}
    </label>
  )
}

function PreviewButton({ setCurrentTrack, isSelected }) {
  const { play, pause, isPaused } = useContext(AudioContext)
  return (
    <button
      type="button"
      className={styles.button}
      style={{ '--bg': '#cc4dff' }}
      onClick={() => {
        if (isSelected && !isPaused) {
          pause()
        } else {
          setCurrentTrack()
          setTimeout(play, 100)
        }
      }}
    >
      {isSelected && !isPaused ? 'Stop Preview' : 'Preview Track'}
    </button>
  )
}

export const MEDIA_OPTS = [
  {
    label: 'Online / Web / Streaming',
    value: 'Online / Web / Streaming',
  },
  { label: 'In Store', value: 'In Store' },
  { label: 'Tv', value: 'Tv' },
  { label: 'Cinema', value: 'Cinema' },
  { label: 'Film Festival', value: 'Film Festival' },
  { label: 'Event & Trade Show', value: 'Event & Trade Show' },
  {
    label: 'Personal Event (wedding, anniversary)',
    value: 'Personal Event (wedding, anniversary)',
  },
  { label: 'Podcast', value: 'Podcast' },
  { label: 'Corporate Intern Network', value: 'Corporate Intern Network' },
  { label: 'Social Media', value: 'Social Media' },
  { label: 'VOD (video on demand)', value: 'VOD (video on demand)' },
  { label: 'DVD', value: 'DVD' },
  { label: 'Radio', value: 'Radio' },
  { label: 'Student', value: 'Student' },
  { label: 'Other', value: 'Other' },
]

const PAIDMEDIA_OPTS = [
  { label: 'Paid Media', value: 'Paid Media' },
  {
    label: 'Without Paid Media',
    value: 'Without Paid Media',
  },
]

export const PROJECT_TYPE_OPTS = [
  { label: 'Advertising', value: 'Advertising' },
  { label: 'Short Film', value: 'Short Film' },
  { label: 'Reel', value: 'Reel' },
  { label: 'Independent Film', value: 'Independent Film' },
  { label: 'Feature Film', value: 'Feature Film' },
  { label: 'Trailer', value: 'Trailer' },
  { label: 'Serie', value: 'Serie' },
  { label: 'Teaser, Intro-Video', value: 'Teaser, Intro-Video' },
  { label: 'Generic Movie', value: 'Generic Movie' },
  { label: 'Branded Content', value: 'Branded Content' },
  { label: 'Video Game', value: 'Video Game' },
  { label: 'Personal', value: 'Personal' },
]

export const TERRITORY_OPTS = [
  { label: 'Worldwide', value: 'Worldwide' },
  { label: 'National', value: 'National' },
  { label: 'Regional', value: 'Regional' },
  { label: 'Local', value: 'Local' },
]

const TYPEOFUSE_OPTS = [
  { label: 'Public Use', value: 'Public Use' },
  { label: 'Private Use', value: 'private' },
]

const LICENSINGTERM_OPTS = [
  { label: '1 Month', value: '1 Month' },
  { label: '6 Months', value: '6 Months' },
  { label: '1 Year', value: '1 Year' },
  { label: 'Perpetual', value: 'Perpetual' },
]

const TYPEOFLICENSE_OPTS = [
  { label: 'Non-Exclusive License', value: 'Non-Exclusive License' },
  { label: 'Exclusive License', value: 'Exclusive License' },
]

export const COUNTRIES_OPTS = countries.map(({ code, name }) => ({
  label: name,
  value: code,
}))

const STYLE_OPTS = genre.sort().map((g) => ({ label: g, value: g }))
const VOCALS_OPTS = vocals.map((v) => ({ label: v, value: v }))
const TEMPO_OPTS = tempo.map((t) => ({ label: t, value: t }))

const ARTIST_OPT = Object.keys(tracksByArtists).map((artist) => ({
  label: artist,
  value: artist,
}))
